import { useState, useEffect, useMemo } from 'react';
import { LLMModel } from '../app/store/constants/llmModels';
import { ContractInfo } from '../app/store/api/ai-api';

// ─────────────────────────────────────────────────────────────────────────────
// useModelSwitcher
//
// Manages LLM overrides per chatbot:
// - Claim & Contract chatbots: Allow selecting TWO models (one Explanatory, one Reasoning)
// - Claim Analysis: Single-selection only (picking one deselects the other category)
// ─────────────────────────────────────────────────────────────────────────────

export type ChatbotType   = 'contract' | 'claim' | 'claim_analysis';
export type ModelCategory = 'reasoning' | 'explanatory';

export interface ModelSelection {
    reasoningModel: LLMModel | null;
    quickModel: LLMModel | null; // renamed internally to quickModel but maps to explanatory_model in API
}

export interface AvailableModelsResponse {
    explanatory: LLMModel[];
    reasoning: LLMModel[];
}

const SESSION_KEY = (type: ChatbotType) => `slicediq_llm_override_v3_${type}`;

function readSession(type: ChatbotType): ModelSelection | null {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY(type));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as any;
        
        if (parsed && (parsed.reasoningModel || parsed.quickModel)) {
            return parsed as ModelSelection;
        }
        
        sessionStorage.removeItem(SESSION_KEY(type));
        return null;
    } catch {
        return null;
    }
}

function writeSession(type: ChatbotType, data: ModelSelection): void {
    try {
        sessionStorage.setItem(SESSION_KEY(type), JSON.stringify(data));
    } catch { /* QuotaExceededError — not critical */ }
}

export interface UseModelSwitcherReturn {
    reasoningModel: LLMModel | null;
    quickModel: LLMModel | null;
    availableModels: AvailableModelsResponse;
    isPending: boolean;
    /** Select a model for a specific category */
    selectModel: (model: LLMModel, category: ModelCategory) => void;
    /** Clear all overrides for this chatbot */
    resetToDefault: () => void;
    /** True if any categorization is overridden */
    isOverridden: boolean;
}

const INITIAL_STATE: ModelSelection = {
    reasoningModel: null,
    quickModel: null,
};

export function useModelSwitcher(chatType: ChatbotType, isEnabled: boolean): UseModelSwitcherReturn {
    const isDualEnabled = chatType === 'contract' || chatType === 'claim';
    const [availableModels, setAvailableModels] = useState<AvailableModelsResponse>({ explanatory: [], reasoning: [] });
    const [isPending, setIsPending] = useState(isEnabled);
    
    // Selection state (overrides)
    const [selection, setSelection] = useState<ModelSelection>(isEnabled ? (readSession(chatType) || INITIAL_STATE) : INITIAL_STATE);

    // Fetch models on mount
    useEffect(() => {
        if (!isEnabled) {
            setIsPending(false);
            setSelection(INITIAL_STATE);
            return;
        }
        const api = new ContractInfo();
        api.getAvailableModels().then(res => {
            if (res) {
                setAvailableModels(res);
                
                // If no session override exists, pre-load platform defaults
                const saved = readSession(chatType);
                if (!saved) {
                    const defaultExplanatory = res.explanatory?.find((m: any) => m.default);
                    const defaultReasoning = res.reasoning?.find((m: any) => m.default);
                    
                    if (isDualEnabled) {
                        if (defaultExplanatory || defaultReasoning) {
                            setSelection({
                                reasoningModel: defaultReasoning || null,
                                quickModel: defaultExplanatory || null
                            });
                        }
                    } else {
                        // Single selection mode: pick one default (prefer reasoning)
                        setSelection({
                            reasoningModel: defaultReasoning || null,
                            quickModel: (!defaultReasoning && defaultExplanatory) ? defaultExplanatory : null
                        });
                    }
                }
            }
            setIsPending(false);
        }).catch(() => setIsPending(false));
    }, [chatType, isDualEnabled, isEnabled]);

    const selectModel = (model: LLMModel, category: ModelCategory) => {
        setSelection(prev => {
            let next: ModelSelection;
            
            if (isDualEnabled) {
                next = {
                    ...prev,
                    [category === 'reasoning' ? 'reasoningModel' : 'quickModel']: model,
                };
            } else {
                next = {
                    reasoningModel: category === 'reasoning' ? model : null,
                    quickModel: category === 'explanatory' ? model : null,
                };
            }
            
            writeSession(chatType, next);
            return next;
        });
    };

    const resetToDefault = () => {
        // Find defaults from available models to reset properly
        const defaultExplanatory = availableModels.explanatory?.find((m: any) => m.default) || null;
        const defaultReasoning = availableModels.reasoning?.find((m: any) => m.default) || null;
        
        let resetState: ModelSelection;
        if (isDualEnabled) {
            resetState = {
                reasoningModel: defaultReasoning,
                quickModel: defaultExplanatory,
            };
        } else {
            // Single mode: pick one
            resetState = {
                reasoningModel: defaultReasoning,
                quickModel: (!defaultReasoning && defaultExplanatory) ? defaultExplanatory : null
            };
        }
        
        setSelection(resetState);
        sessionStorage.removeItem(SESSION_KEY(chatType));
    };

    // isOverridden if selection differs from the "default: true" models
    // But for now, user just asked to respect default: true as pre-selected.
    // If we want "isOverridden" to mean "user manually changed it", we'd need to track that.
    // Let's assume for now any selection exists is fine.
    const isOverridden = Boolean(selection.reasoningModel || selection.quickModel);

    return {
        reasoningModel: selection.reasoningModel,
        quickModel: selection.quickModel,
        availableModels,
        isPending,
        selectModel,
        resetToDefault,
        isOverridden,
    };
}
